import { Component, Element, Event, EventEmitter, h, Prop, State, Watch } from '@stencil/core';

@Component({
  tag: 'pk-countdown-timer',
  styleUrl: './pk-countdown-timer.scss',
  shadow: true,
})
export class PkCountdownTimer {
  // Own Properties
  // ====================

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkCountdownTimerElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() deadlineDate: Date;
  @State() remaningDays: string = '00';
  @State() remaningHours: string = '00';
  @State() remaningMinutes: string = '00';
  @State() remaningSeconds: string = '00';
  @State() timeIntervalId: NodeJS.Timeout;
  @State() formatObject: { HH: boolean; DD: boolean; mm: boolean; ss: boolean } = {
    HH: true,
    DD: true,
    mm: true,
    ss: true,
  };

  // Public Property API
  // ========================

  /** The future date on when the timer will be set to `00:00:00:00`. */
  @Prop({ reflect: true }) deadline!: string | Date;

  /** It will specify what elements the countdown-timer component should shown.
   * E.g: `format="HH:mm"` means that only the `Hours` and `Minutes` items will be shown in the timer. */
  @Prop() format: string = 'DD:HH:mm:ss';

  /** Text wording for the `Days` label.
   * Passing `false` as value, e.g: days-label="false" will hide the label. */
  @Prop() daysLabel: string = 'Days';

  /** Text wording for the `Hours` label.
   * Passing `false` as value, e.g: hours-label="false" will hide the label. */
  @Prop() hoursLabel: string = 'Hours';

  /** Text wording for the `Minutes` label.
   * Passing `false` as value, e.g: minutes-label="false" will hide the label. */
  @Prop() minutesLabel: string = 'Minutes';

  /** Text wording for the `Seconds` label.
   * Passing `false` as value, e.g: seconds-label="false" will hide the label. */
  @Prop() secondsLabel: string = 'Seconds';

  // Prop lifecycle events
  // =======================

  @Watch('deadline')
  handleDeadlineChange() {
    // Make sure to clear interval time if the deadline property change
    // after the component has been loaded
    this.clearTimeInterval();

    if (!this.deadline) {
      this.resetTimerValues();
      console.error(`[pk-countdown-timer]: "deadline" property value is missing 😵!!`);
      return;
    }

    this.deadlineDate = this.deadline instanceof Date ? this.deadline : new Date(this.deadline);
    if (!this.isValidDate(this.deadlineDate)) {
      this.resetTimerValues();
      console.error(`[pk-countdown-timer]: "deadline" property have an invalid Date string/object value 🚫!!`);
      return;
    }

    this.setTimeInterval();
  }

  @Watch('format')
  handleFormatChange() {
    const arrayObj = this.format.split(':');
    this.formatObject = {
      DD: arrayObj.includes('DD'),
      HH: arrayObj.includes('HH'),
      mm: arrayObj.includes('mm'),
      ss: arrayObj.includes('ss'),
    };
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** Callback handler to be called when the deadline ends, timer get down to 00:00:00:00 */
  @Event() pkDeadlineEnds: EventEmitter;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  componentDidLoad() {
    this.handleFormatChange();
    this.handleDeadlineChange();
  }

  disconnectedCallback() {
    this.clearTimeInterval();
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private isValidDate = (value: Date | string) => value instanceof Date && !isNaN(value.valueOf());

  private updateClock = () => {
    if (!this.deadlineDate) return;

    const t = this.getTimeRemaining(this.deadlineDate);

    if (t.distance <= 0) {
      this.resetTimerValues();
      this.clearTimeInterval();

      this.pkDeadlineEnds.emit();
      return;
    }

    // Handle leading zeros
    this.remaningDays = `${t.days}`.length < 2 ? `0${t.days}` : `${t.days}`;
    this.remaningHours = `0${t.hours}`.slice(-2);
    this.remaningMinutes = `0${t.minutes}`.slice(-2);
    this.remaningSeconds = `0${t.seconds}`.slice(-2);
  };

  private setTimeInterval = () => {
    this.timeIntervalId = setInterval(this.updateClock, 1000);
  };

  private clearTimeInterval = () => {
    clearInterval(this.timeIntervalId);
  };

  private getTimeRemaining = (time: Date) => {
    // Get today's date and time.
    const now = new Date().getTime();
    // Find the distance between now and the `deadline` date.
    const distance = time.getTime() - now;

    // Time calculations for days, hours, minutes and seconds.
    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);

    return {
      distance,
      days,
      hours,
      minutes,
      seconds,
    };
  };

  private resetTimerValues = () => {
    this.remaningDays = '00';
    this.remaningHours = '00';
    this.remaningMinutes = '00';
    this.remaningSeconds = '00';
  };

  // private getAllVisibleItems = () => this.timerRefElem.querySelectorAll('pk-countdown-item');

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    return (
      <div class="countdown-timer--container" part="timer">
        {this.formatObject.DD && (
          <pk-countdown-item class="mr-[--countdown-item--margin-right] last-of-type:mr-0" part="days">
            <span>{this.remaningDays}</span>
            {this.daysLabel !== 'false' && <span slot="label">{this.daysLabel}</span>}
          </pk-countdown-item>
        )}
        {this.formatObject.HH && (
          <pk-countdown-item class="mr-[--countdown-item--margin-right] last-of-type:mr-0" part="hours">
            <span>{this.remaningHours}</span>
            {this.hoursLabel !== 'false' && <span slot="label">{this.hoursLabel}</span>}
          </pk-countdown-item>
        )}
        {this.formatObject.mm && (
          <pk-countdown-item class="mr-[--countdown-item--margin-right] last-of-type:mr-0" part="minutes">
            <span>{this.remaningMinutes}</span>
            {this.minutesLabel !== 'false' && <span slot="label">{this.minutesLabel}</span>}
          </pk-countdown-item>
        )}
        {this.formatObject.ss && (
          <pk-countdown-item class="mr-[--countdown-item--margin-right] last-of-type:mr-0" part="seconds">
            <span>{this.remaningSeconds}</span>
            {this.secondsLabel !== 'false' && <span slot="label">{this.secondsLabel}</span>}
          </pk-countdown-item>
        )}
      </div>
    );
  }
}
